/**
 * ThemeVars
 *
 * Injects CSS custom properties for all design tokens onto :root.
 * Uses @media (prefers-color-scheme) for automatic dark mode.
 */

import { Global, css, type SerializedStyles } from "@emotion/react";
import { lightTheme } from "./themes/lightTheme";
import { darkTheme } from "./themes/darkTheme";
import type { Theme, ThemeColors } from "./themes/types";
import { fontFamily } from "./primitives/typography";
import { spacingValues } from "./primitives/spacing";
import { fontSizeValues, fontWeightValues, lineHeightValues, letterSpacingValues } from "./primitives/typography";
import { borderRadiusValues } from "./primitives/shape";
import { durationValues } from "./primitives/animation";
import { opacityValues } from "./primitives/opacity";
import { layoutValues } from "./primitives/layout";
import { text } from "./semantic/colors";

/** Generate --sem-* and --sem-shadow-* declarations from a Theme. */
function generateSemanticVars(theme: Theme): string {
  const lines: string[] = [];
  const c = theme.colors;

  // Text
  for (const [key, value] of Object.entries(c.text)) {
    lines.push(`--sem-text-${key}: ${value};`);
  }
  // Surface
  for (const [key, value] of Object.entries(c.surface)) {
    lines.push(`--sem-surface-${key}: ${value};`);
  }
  // Border
  for (const [key, value] of Object.entries(c.border)) {
    lines.push(`--sem-border-${key}: ${value};`);
  }
  // Interactive
  for (const [key, value] of Object.entries(c.interactive)) {
    lines.push(`--sem-interactive-${key}: ${value};`);
  }
  // Status
  for (const [key, value] of Object.entries(c.status)) {
    lines.push(`--sem-status-${key}: ${value};`);
  }
  // Brand
  for (const [key, value] of Object.entries(c.brand)) {
    lines.push(`--sem-brand-${key}: ${value};`);
  }
  // Nav
  for (const [key, value] of Object.entries(c.nav)) {
    lines.push(`--sem-nav-${key}: ${value};`);
  }
  // Footer
  for (const [key, value] of Object.entries(c.footer)) {
    lines.push(`--sem-footer-${key}: ${value};`);
  }
  // Shadows
  for (const [key, value] of Object.entries(theme.shadow)) {
    lines.push(`--sem-shadow-${key}: ${value};`);
  }

  return lines.join("\n    ");
}

/** Static (theme-independent) token vars — injected once on :root */
function generateStaticVars(): string {
  const lines: string[] = [];

  for (const [key, value] of Object.entries(spacingValues)) {
    lines.push(`--sp-${key}: ${value};`);
  }
  for (const [key, value] of Object.entries(fontSizeValues)) {
    lines.push(`--fs-${key}: ${value};`);
  }
  // Semantic control font sizes
  lines.push(`--fs-control: var(--fs-lg);`);
  lines.push(`--fs-control-sm: var(--fs-sm);`);

  for (const [key, value] of Object.entries(fontWeightValues)) {
    lines.push(`--fw-${key}: ${value};`);
  }
  for (const [key, value] of Object.entries(lineHeightValues)) {
    lines.push(`--lh-${key}: ${value};`);
  }
  for (const [key, value] of Object.entries(borderRadiusValues)) {
    lines.push(`--br-${key}: ${value};`);
  }
  for (const [key, value] of Object.entries(letterSpacingValues)) {
    lines.push(`--ls-${key}: ${value};`);
  }
  for (const [key, value] of Object.entries(durationValues)) {
    lines.push(`--dur-${key}: ${value};`);
  }
  for (const [key, value] of Object.entries(opacityValues)) {
    lines.push(`--op-${key}: ${value};`);
  }
  for (const [key, value] of Object.entries(layoutValues)) {
    lines.push(`--lay-${key}: ${value};`);
  }

  return lines.join("\n    ");
}

const lightVars = generateSemanticVars(lightTheme);
const darkVars = generateSemanticVars(darkTheme);
const staticVars = generateStaticVars();

const themeStyles = css`
  :root {
    ${staticVars}
    ${lightVars}
    color-scheme: light dark;
  }

  @media (prefers-color-scheme: dark) {
    :root {
      ${darkVars}
    }
  }
`;

/**
 * Plain-string version of the theme CSS, used to render a `<style>` tag in
 * the document `<head>` during prerender. Embedding the CSS variables and
 * base resets inline at render time (via `<Global>`) puts them in `<body>`,
 * which causes a flash of unstyled content before the browser parses past
 * those tags. Emitting them in `<head>` avoids the flash.
 */
export function getThemeCSS(theme?: Theme): string {
  const themeVars = theme
    ? `:root {
    ${staticVars}
    ${generateSemanticVars(theme)}
    color-scheme: ${theme.name === "dark" ? "dark" : "light"};
  }`
    : `:root {
    ${staticVars}
    ${lightVars}
    color-scheme: light dark;
  }
  @media (prefers-color-scheme: dark) {
    :root {
      ${darkVars}
    }
  }`;

  const base = `html {
    box-sizing: border-box;
    font-family: "${fontFamily.base}", sans-serif;
  }
  body {
    margin: 0;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
  a {
    color: ${text.link};
    text-decoration: none;
  }
  .italicize { font-style: italic; }
  .bold { font-weight: bold; }
  code {
    font-family: source-code-pro, Menlo, Monaco, Consolas, "Courier New", monospace;
  }`;

  return `${themeVars}\n${base}`;
}

/**
 * Forces light theme CSS variables within a scoped container,
 * overriding the dark mode media query.
 */
export const lightThemeOverride: SerializedStyles = css`
  @media (prefers-color-scheme: dark) {
    ${lightVars}
    color-scheme: light;
  }
`;

const baseStyles = css`
  html {
    box-sizing: border-box;
    font-family: "${fontFamily.base}", sans-serif;
  }

  body {
    margin: 0;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  a {
    color: ${text.link};
    text-decoration: none;
  }

  .italicize {
    font-style: italic;
  }

  .bold {
    font-weight: bold;
  }

  code {
    font-family: source-code-pro, Menlo, Monaco, Consolas, "Courier New",
      monospace;
  }
`;

interface ThemeVarsProps {
  theme?: Theme;
}

const ThemeVars: React.FC<ThemeVarsProps> = ({ theme }) => {
  const styles = theme
    ? css`
        :root {
          ${staticVars}
          ${generateSemanticVars(theme)}
          color-scheme: ${theme.name === "dark" ? "dark" : "light"};
        }
      `
    : themeStyles;

  return (
    <>
      <Global styles={styles} />
      <Global styles={baseStyles} />
    </>
  );
};

export default ThemeVars;
